/**
 * use-ai-train-test.ts — API cho khung "Kiểm tra hỏi đáp" / "Hướng dẫn AI" và trang Phân tích của
 * nhân viên AI (màn Dạy nhân viên AI).
 *
 * Backend: simulate-routes.ts (reply + detail, turn/:runId, explain, feedback), master-routes.ts
 * (AI Master sessions + proposals), train-analytics-routes.ts, kb-qa-routes.ts.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'

// ── Chi tiết một lượt trả lời ────────────────────────────────────────

export interface DetailStep {
  kind: 'thought' | 'tool' | 'check' | 'answer' | 'handoff' | 'error'
  title: string
  summary?: string
  tool?: string
  input?: unknown
  output?: unknown
  latencyMs?: number | null
  /** search_knowledge: ngưỡng điểm đã áp (0..1). */
  threshold?: number
}

export interface DetailSource {
  id?: string
  title: string
  /** faq | article | product | order */
  type: string
  score: number | null
  scorePct: number | null
  match?: 'semantic' | 'keyword' | 'both' | string
  snippet?: string
  matchedQuestion?: string | null
  tool: string
  canonicalQuestion?: string | null
  similarQuestions?: string[]
  status?: string
  categoryName?: string | null
  /** Kho tri thức chứa mục — hiện "[Hỏi đáp · <tên kho>]". */
  knowledgeBaseName?: string | null
}

export interface TurnDetail {
  steps: DetailStep[]
  sources: DetailSource[]
  bubbles: string[]
  /** Ngưỡng điểm tri thức đã dùng ở lượt này (null = không tìm tri thức). */
  threshold?: number | null
}

/** Chat thử: AI ngừng trả lời theo "Tự động ngừng trả lời" (không gọi AI ở lượt này). */
export interface AutoStopNotice {
  /** schedule = trong khung thời gian dừng; already = đã ngừng từ trước trong phiên; condition = vừa khớp điều kiện. */
  kind: 'schedule' | 'already' | 'condition'
  reason: string
  closingMessage: string | null
  closingImages: string[]
}

export interface SimulateResult {
  runId?: string
  reply: string | null
  bubbles?: string[]
  handoff?: { should: boolean; reason?: string } | null
  /** Câu báo khách khi chuyển người (theo cài đặt của bản đang thử). */
  handoffNotice?: string | null
  /** "Trả lời kỹ lưỡng": câu chờ gửi trước câu trả lời. */
  waitNotice?: string | null
  /** "AI giống người thật" của bản đang thử — chat thử không chờ thật, chỉ báo lại. */
  humanLike?: { replyDelay: { min: number; max: number } | null; bubbleDelay: { min: number; max: number } } | null
  trace?: Array<{ step: string; payload: unknown }>
  detail?: TurnDetail
  autoStop?: AutoStopNotice
}

export async function createTestConversation(name: string) {
  const { data } = await api.post('/ai/simulate/conversation', { name, aiMode: 'suggest' })
  return data as { conversationId: string }
}

export async function simulateTurn(input: { conversationId: string; customerText: string; botId: string; contactId?: string | null }) {
  const { data } = await api.post('/ai/simulate/reply', {
    conversationId: input.conversationId, customerText: input.customerText, botId: input.botId,
    includeTrace: true, ...(input.contactId ? { contactId: input.contactId } : {}),
  })
  return data as SimulateResult
}

export async function fetchTurnDetail(runId: string) {
  const { data } = await api.get(`/ai/simulate/turn/${runId}`)
  return data as { runId: string; detail: TurnDetail }
}

export type ExplainQuick = 'analyze' | 'no_media' | 'why' | 'not_follow_kb'
export const EXPLAIN_QUICK_LABELS: Record<ExplainQuick, string> = {
  get analyze() { return i18n.t('aiTrainLib:useAiTrainTest.quick.analyze') },
  get no_media() { return i18n.t('aiTrainLib:useAiTrainTest.quick.no_media') },
  get why() { return i18n.t('aiTrainLib:useAiTrainTest.quick.why') },
  get not_follow_kb() { return i18n.t('aiTrainLib:useAiTrainTest.quick.not_follow_kb') },
}

export interface ExplainSuggestion {
  kind: 'edit_knowledge' | 'add_faq' | 'edit_persona' | 'other'
  title: string
  detail: string
  entryId?: string
  question?: string
  answer?: string
}

export interface ExplainResult { question: string; answer: string; suggestions: ExplainSuggestion[] }

export async function explainTurn(input: {
  runId: string; botId?: string; customerText: string; reply: string | null; handoffReason?: string | null
  quick?: ExplainQuick; question?: string; history?: Array<{ role: 'user' | 'assistant'; content: string }>
}) {
  const { data } = await api.post('/ai/simulate/explain', input)
  return data as ExplainResult
}

export async function saveTurnFeedback(input: {
  runId?: string; customerText: string; correctAnswer: string; saveAs: 'faq'
  categoryId?: string | null; similarQuestions?: string[]; originalReply?: string | null
  /** Kho nhận hỏi đáp. Không chọn → máy chủ lưu vào kho đầu tiên nhân viên AI đang tra / "Kho mặc định". */
  knowledgeBaseId?: string | null
  botId?: string | null
}) {
  const { data } = await api.post('/ai/simulate/feedback', input)
  return data as { saved: 'faq'; id: string; status?: string; categoryId?: string | null; knowledgeBaseId?: string; knowledgeBaseName?: string | null }
}

// ── Hướng dẫn AI (AI Master) ─────────────────────────────────────────

export async function openGuideSession(contextConversationId?: string | null, botId?: string | null) {
  const { data } = await api.post('/ai/master/sessions', {
    ...(contextConversationId ? { contextConversationId } : {}),
    ...(botId ? { botId } : {}),
  })
  return (data as { session: { id: string } }).session
}

export async function sendGuideMessage(sessionId: string, text: string) {
  const { data } = await api.post(`/ai/master/sessions/${sessionId}/messages`, { text })
  return data as { reply: string; proposalId?: string }
}

export interface GuideProposal {
  id: string
  targetType: string
  targetSubtype?: string | null
  targetId?: string | null
  currentValue?: string | null
  proposedValue: string
  rationale: string
  status: string
}

export async function fetchPendingProposal(id: string): Promise<GuideProposal | null> {
  const { data } = await api.get('/ai/master/proposals', { params: { status: 'pending', limit: 50 } })
  return ((data as { items: GuideProposal[] }).items ?? []).find((p) => p.id === id) ?? null
}

export async function applyProposal(id: string) {
  const { data } = await api.post<{ proposalId: string; appliedRef: string | null }>(`/ai/master/proposals/${id}/apply`)
  return data
}

/** Sự kiện: nháp của nhân viên AI vừa bị sửa từ nơi khác (vd áp đề xuất Hướng dẫn AI) → màn Dạy AI tải lại. */
export const TRAIN_BOT_RELOAD_EVENT = 'ecdp:ai-train-bot-reload'

export async function rejectProposal(id: string) {
  const { data } = await api.post(`/ai/master/proposals/${id}/reject`)
  return data
}

// ── Phân tích ────────────────────────────────────────────────────────

export interface AnalyticsOverview {
  botName: string
  range: { from: string; to: string }
  turns: number
  replies: number
  handoffTurns: number
  skipped: number
  errors: number
  avgLatencyMs: number | null
  conversations: number
  customers: number
  handoffConversations: number
  resolvedConversations: number
  handoffRate: number | null
  resolvedRate: number | null
  daily: Array<{ day: string; turns: number; replies: number; handoffs: number; conversations: number }>
}

export type AnalyticsMessageStatus = 'answered' | 'handoff' | 'skipped' | 'error'

export interface AnalyticsMessage {
  runId: string
  at: string
  status: AnalyticsMessageStatus
  mode: string
  latencyMs: number | null
  conversationId: string
  customer: string | null
  channel: string | null
  question: string | null
  answer: string | null
  answerSent: boolean
  handoffReason: string | null
  basis: Array<{ title: string; type?: string; score: number | null }>
  threshold: number | null
}

export interface AnalyticsConversation {
  conversationId: string
  customer: string | null
  channelId: string
  channel: string | null
  firstAt: string
  lastAt: string
  turns: number
  replies: number
  handoff: boolean
  resolved: boolean
}

export type AnalyticsRange = { from: string; to: string }

export function useAnalyticsOverview(botId: string | undefined, range: AnalyticsRange) {
  return useQuery<AnalyticsOverview>({
    queryKey: ['ai', 'analytics', botId, 'overview', range],
    queryFn: async () => (await api.get(`/ai/bots/${botId}/analytics/overview`, { params: range })).data,
    enabled: !!botId,
  })
}

export async function fetchAnalyticsMessages(botId: string, params: AnalyticsRange & { status?: string; search?: string; limit?: number; offset?: number }) {
  const { data } = await api.get(`/ai/bots/${botId}/analytics/messages`, { params })
  return data as { total: number; items: AnalyticsMessage[] }
}

export function useAnalyticsMessages(botId: string | undefined, params: AnalyticsRange & { status?: string; search?: string; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['ai', 'analytics', botId, 'messages', params],
    queryFn: () => fetchAnalyticsMessages(botId!, params),
    enabled: !!botId,
    placeholderData: (prev) => prev,
  })
}

export async function fetchAnalyticsConversations(botId: string, params: AnalyticsRange & { handoff?: string; resolved?: string; limit?: number; offset?: number }) {
  const { data } = await api.get(`/ai/bots/${botId}/analytics/conversations`, { params })
  return data as { total: number; items: AnalyticsConversation[] }
}

export function useAnalyticsConversations(botId: string | undefined, params: AnalyticsRange & { handoff?: string; resolved?: string; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['ai', 'analytics', botId, 'conversations', params],
    queryFn: () => fetchAnalyticsConversations(botId!, params),
    enabled: !!botId,
    placeholderData: (prev) => prev,
  })
}

// ── Hỏi đáp: bật/tắt, nhập/xuất CSV ──────────────────────────────────

export async function setKnowledgeEnabled(id: string, enabled: boolean) {
  const { data } = await api.post(`/knowledge/${id}/enabled`, { enabled })
  return data
}

export async function importQaCsv(csv: string, categoryId?: string | null, knowledgeBaseId?: string | null) {
  const { data } = await api.post('/knowledge/qa/import', { csv, categoryId: categoryId || null, knowledgeBaseId: knowledgeBaseId || null })
  return (data as { data: { created: number; updated: number; errors: Array<{ line: number; message: string }>; unknownCategories: string[] } }).data
}

export async function exportQaCsv(categoryId?: string | null, knowledgeBaseId?: string | null): Promise<Blob> {
  const { data } = await api.get('/knowledge/qa/export', {
    params: { ...(categoryId ? { categoryId } : {}), ...(knowledgeBaseId ? { knowledgeBaseId } : {}) }, responseType: 'blob',
  })
  return data as Blob
}
